import { z } from 'zod';
const cents = z.number().int().min(0).max(100_000_000_00);
const bp = (max) => z.number().int().min(0).max(max);
// ------------------------------------------------------------ tabela de preços
export const PRICE_UNITS = ['fixed', 'per_graft', 'per_session'];
export const priceItemSchema = z.object({
    id: z.uuid(), name: z.string(), description: z.string().nullable(), unit: z.enum(PRICE_UNITS),
    priceCents: z.number().int(), active: z.boolean(), position: z.number().int(),
});
export const upsertPriceItemSchema = z.object({
    name: z.string().trim().min(2, 'Dê um nome ao item').max(80),
    description: z.string().trim().max(280).nullable().default(null),
    unit: z.enum(PRICE_UNITS),
    priceCents: cents,
    active: z.boolean().default(true),
});
// ------------------------------------------------------------ regras comerciais
export const billingSettingsSchema = z.object({
    /** desconto máximo sem aprovação, em pontos-base (1000 = 10%) */
    maxDiscountBp: bp(10000),
    validityDays: z.number().int().min(1).max(180),
    maxInstallments: z.number().int().min(1).max(24),
    /** desconto para pagamento à vista (Pix), em pontos-base */
    cashDiscountBp: bp(3000),
    terms: z.string().trim().max(2000).nullable(),
});
// ------------------------------------------------------------ orçamento
export const QUOTE_STATUSES = ['draft', 'sent', 'accepted', 'expired', 'cancelled'];
/** Item escolhido da tabela (preço vem da tabela) ou avulso (descrição e preço livres). */
export const quoteItemInputSchema = z.union([
    z.object({ priceItemId: z.uuid(), quantity: z.number().int().min(1).max(10000) }),
    z.object({ description: z.string().trim().min(2).max(120), unitPriceCents: cents, quantity: z.number().int().min(1).max(10000) }),
]);
export const quoteVersionInputSchema = z.object({
    items: z.array(quoteItemInputSchema).min(1, 'Inclua pelo menos um item').max(30),
    discountCents: cents.default(0),
    discountReason: z.string().trim().max(280).nullable().default(null),
    maxInstallments: z.number().int().min(1).max(24).optional(),
    cashDiscountBp: bp(3000).optional(),
    /** data de validade (padrão: hoje + validade da clínica) */
    validUntil: z.iso.date().optional(),
    notes: z.string().trim().max(2000).nullable().default(null),
});
export const createQuoteSchema = quoteVersionInputSchema.extend({ evaluationId: z.uuid() });
export const quoteItemSchema = z.object({
    priceItemId: z.uuid().nullable(), description: z.string(), unit: z.enum(PRICE_UNITS),
    quantity: z.number().int(), unitPriceCents: z.number().int(), totalCents: z.number().int(),
});
export const DISCOUNT_STATES = ['none', 'within_limit', 'pending', 'approved', 'rejected'];
export const quoteVersionSchema = z.object({
    version: z.number().int(),
    items: z.array(quoteItemSchema),
    subtotalCents: z.number().int(),
    discountCents: z.number().int(),
    totalCents: z.number().int(),
    discountBp: z.number().int(),
    discountReason: z.string().nullable(),
    discount: z.object({
        state: z.enum(DISCOUNT_STATES),
        decidedByName: z.string().nullable(),
        reason: z.string().nullable(),
    }),
    maxInstallments: z.number().int(),
    cashDiscountBp: z.number().int(),
    /** total à vista com o desconto do Pix */
    cashTotalCents: z.number().int(),
    validUntil: z.string(),
    notes: z.string().nullable(),
    createdByName: z.string(),
    createdAt: z.string(),
});
export const quoteSummarySchema = z.object({
    id: z.uuid(),
    code: z.string(),
    patientId: z.uuid(),
    patientName: z.string(),
    status: z.enum(QUOTE_STATUSES),
    totalCents: z.number().int(),
    version: z.number().int(),
    discountPending: z.boolean(),
    validUntil: z.string(),
    createdAt: z.string(),
    sentAt: z.string().nullable(),
    acceptedAt: z.string().nullable(),
});
export const quoteSchema = quoteSummarySchema.extend({
    evaluation: z.object({
        id: z.uuid(), doctorName: z.string(), signedAt: z.string(),
        graftsEstimateMin: z.number().int().nullable(), graftsEstimateMax: z.number().int().nullable(), technique: z.string().nullable(),
    }),
    current: quoteVersionSchema,
    versions: z.array(z.object({ version: z.number().int(), totalCents: z.number().int(), createdAt: z.string(), createdByName: z.string() })),
    sentVersion: z.number().int().nullable(),
    acceptedVersion: z.number().int().nullable(),
    cancelReason: z.string().nullable(),
    terms: z.string().nullable(),
    /** o que a pessoa logada pode fazer agora */
    can: z.object({ edit: z.boolean(), send: z.boolean(), accept: z.boolean(), cancel: z.boolean(), decideDiscount: z.boolean() }),
});
export const sendQuoteSchema = z.object({ whatsapp: z.boolean().default(true) });
export const discountDecisionSchema = z.object({ decision: z.enum(['approved', 'rejected']), reason: z.string().trim().max(280).nullable().default(null) });
export const cancelQuoteSchema = z.object({ reason: z.string().trim().min(3, 'Diga o motivo').max(280) });
/** Paciente com avaliação assinada, apto a receber orçamento (sem dado clínico além do necessário para precificar). */
export const quotableEvaluationSchema = z.object({
    evaluationId: z.uuid(), patientId: z.uuid(), patientName: z.string(), pseudonym: z.string(),
    doctorName: z.string(), signedAt: z.string(),
    graftsEstimateMin: z.number().int().nullable(), graftsEstimateMax: z.number().int().nullable(), technique: z.string().nullable(),
    openQuotes: z.number().int(),
});
export const formatBRL = (c) => (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
export const quoteCode = (n) => `ORC-${String(n).padStart(4, '0')}`;
//# sourceMappingURL=billing.js.map